import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signUp } from '../../services/auth';
import { trackSignupCompleted } from '../../config/analytics';
import { parlorScreen, useDesign } from '../../designs';
import DesignButton from '../../components/ui/DesignButton';
import Input from '../../components/ui/Input';

const d = parlorScreen.tokens;

export default function SignUpScreen() {
  const navigate = useNavigate();
  const { typography } = useDesign();
  const displayFont = `'${typography.displayFont}', serif`;
  const fs = typography.fontSize;
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [betaCode, setBetaCode] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    setIsSubmitting(true);
    try {
      await signUp(email, password, displayName, betaCode);
      trackSignupCompleted(betaCode);
      navigate('/new-trip');
    } catch (err: unknown) {
      const code = err instanceof Error && 'code' in err ? (err as { code: string }).code : '';
      const message = err instanceof Error ? err.message : '';
      if (message === 'Invalid beta code') {
        setError('Invalid beta code');
      } else if (code === 'auth/email-already-in-use') {
        setError('Email already in use');
      } else if (code === 'auth/weak-password') {
        setError('Password must be at least 6 characters');
      } else if (code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else {
        setError('Unable to create account. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: '100vh', padding: '32px',
      backgroundColor: d.bgPrimary,
    }}>
      <div style={{ width: '100%', maxWidth: '380px' }}>
        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <h1 style={{
            fontFamily: displayFont, fontSize: '28px', fontWeight: 700,
            color: d.textPrimary, margin: 0,
          }}>
            KREWE
          </h1>
        </div>

        {/* Screen title */}
        <h2 style={{
          fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 700,
          letterSpacing: '3px', textTransform: 'uppercase',
          color: d.textSecondary, textAlign: 'center', marginBottom: '24px',
        }}>
          Create Account
        </h2>

        {/* Divider */}
        <div style={{
          height: '1px', marginBottom: '24px',
          background: `linear-gradient(to right, transparent, rgba(200,146,58,0.25), transparent)`,
        }} />

        <form onSubmit={handleSubmit}>
          <Input label="Your Name" type="text" value={displayName}
            onChange={(e) => setDisplayName(e.target.value)} required autoComplete="name" />
          <Input label="Email" type="email" value={email}
            onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
          <Input label="Password" type="password" value={password}
            onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password" />
          <Input label="Beta Code" type="text" value={betaCode}
            onChange={(e) => setBetaCode(e.target.value)} required />

          {error && (
            <p style={{
              color: d.negative, fontFamily: "'Raleway', sans-serif",
              fontSize: fs.detail, marginBottom: '16px', textAlign: 'center',
            }}>
              {error}
            </p>
          )}

          <DesignButton tier={2} fullWidth type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Creating account...' : 'Sign Up'}
          </DesignButton>
        </form>

        <div style={{
          textAlign: 'center', marginTop: '24px',
          fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300,
          color: d.textSecondary,
        }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: d.accent, textDecoration: 'none', fontWeight: 500 }}>
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
